import { useQuery } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { appointmentDetailQueryKey } from "../appointmentCaches";
import { fetchAppointment } from "../services/appointmentsService";
import type { ClientAppointment } from "../types/appointments.types";

export interface AppointmentDetailState {
  /**
   * The appointment as the dialog should draw it: the row it was opened with
   * until the read answers, and the server's own copy from then on.
   *
   * Never absent, so the dialog has nothing to hold back for.
   */
  appointment: ClientAppointment;
  /** True while the fresh read is still out and what is drawn is the row. */
  loading: boolean;
  /** The read came back and the record below is the server's. */
  confirmed: boolean;
}

/**
 * One appointment, read by its own `public_id` — `GET /appointments/{id}/`.
 *
 * The dialog is always opened from a row it has just drawn, so it never has
 * nothing to show. What this adds is currency: a diary moves while a screen
 * sits open, and every act the dialog offers — join it, move it, call it off —
 * turns on the record still standing as the list last saw it. So the row it
 * was opened with is drawn immediately and quietly replaced by the server's
 * copy when it lands.
 *
 * `placeholderData` rather than `initialData`, and the difference matters: an
 * initial value would be written into the cache as though it had been fetched,
 * and the read would not be made at all until it went stale. A placeholder is
 * what to draw *while* asking.
 *
 * A failed read leaves the row standing. It was good enough to list a moment
 * ago, and an error state over an appointment somebody can plainly see would
 * be telling them something they have no way to act on.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useAppointmentDetail(
  appointment: ClientAppointment
): AppointmentDetailState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: appointmentDetailQueryKey(appointment.id),
    queryFn: ({ signal }) =>
      fetchAppointment(getToken, appointment.id, signal),
    placeholderData: appointment,
    // The dialog is opened, read and closed in a sitting. Re-asking on every
    // open is the whole point of it; re-asking twice in one is noise.
    staleTime: 15_000,
  });

  return {
    appointment: query.data ?? appointment,
    loading: query.isPending || query.isPlaceholderData,
    confirmed: query.isSuccess && !query.isPlaceholderData,
  };
}

export default useAppointmentDetail;
